import { MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { takeUntil } from 'rxjs';

import { EidosLogSeverity } from '@common/models/core-constant.model';
import { EidosUser } from '@common/models/user-info.model';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosUtilityService } from '@common/services/eidos-utility.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';
import { EidosChangePasswordDialogComponent } from '@common/components/eidos-dialogs/eidos-change-password-dialog/eidos-change-password-dialog.component';
import { CoreModalService, ModalBaseActions } from '@app/core/services/core-modal.service';
import { EidosBaseComponent } from '@common/components/eidos-base.component';

@Component({
  selector: 'eidos-main-page',
  templateUrl: './eidos-main-page.component.html',
  styleUrls: ['./eidos-main-page.component.scss'],
})
export class EidosMainPageComponent extends EidosBaseComponent implements OnInit {
  /**
   * Authenticated flag
   *
   * @memberof EidosMainPageComponent
   */
  public isAuthenticated = false;
  /**
   * CSS class of main page
   *
   * @type {string}
   * @memberof EidosMainPageComponent
   */
  public mainClass: string = 'eidos-theme';
  /**
   * External app mode flag
   *
   * @memberof EidosMainPageComponent
   */
  public extAppMode = false;
  /**
   * Hide topbar flag
   *
   * @type {boolean}
   * @memberof EidosMainPageComponent
   */
  public hideTopbar: boolean = false;

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  mobileToolbarQuery: MediaQueryList;
  private _mobileToolbarQueryListener: () => void;

  protected onPrepareEnvironment = false;

  constructor(
    private securityService: EidosSecurityService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private eidosLogService: EidosLogService,
    private coreModalService: CoreModalService,
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private eidosUtilityService: EidosUtilityService,
  ) {
    super();

    this.mobileQuery = this.media.matchMedia('(max-width: 1024px)');
    this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);

    this.mobileToolbarQuery = this.media.matchMedia('(max-width: 599px)');
    this._mobileToolbarQueryListener = () =>
      this.changeDetectorRef.detectChanges();
    this.mobileToolbarQuery.addListener(this._mobileToolbarQueryListener);

    this.eidosUtilityService.hideTopbar.next(false);
    this.eidosUtilityService.hideSidebar.next(false);
    this.eidosUtilityService.onPrepareEnvironment
      .pipe(takeUntil(this.subscription))
      .subscribe({ next: status => this.onPrepareEnvironment = status });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.eidosUtilityService.hideTopbar
      .pipe(takeUntil(this.subscription))
      .subscribe((hideTopbar) => {
        this.hideTopbar = hideTopbar;
      });

    this.securityService.currentLoggedUser
      .pipe(takeUntil(this.subscription))
      .subscribe((user) => {
        this.eidosLogService.logDebug(
          EidosLogSeverity.Log,
          `User change: ${user.username}`
        );
        this.loadNewUser(user);
      });

    this.extAppMode = this.router.url.indexOf('app') >= 0;
    this.router.events
      .pipe(takeUntil(this.subscription))
      .subscribe((val: any) => {
        if (val.type === EventType.NavigationEnd) {
          this.extAppMode = (val.url?.indexOf('app') ?? 0) >= 0;

          //hide token queryParams
          this.router.navigate(
            [],
            {
              relativeTo: this.activatedRoute,
              queryParams: { token: undefined },
              queryParamsHandling: 'merge',
            }
          );
        }
      });
  }
  /**
   * Load a new user in Eidos
   *
   * @private
   * @param {EidosUser} user
   * @memberof EidosMainPageComponent
   */
  private loadNewUser(user: EidosUser) {
    this.isAuthenticated = user.isValid;

    if (user.useCustomTheme) {
      this.mainClass = 'custom-theme';
    }

    // If user must change password, open the change pwd modal and force to use it
    if (user.mustChangePassword) {
      this.coreModalService.open(EidosChangePasswordDialogComponent, {
        dimensions: { width: '480px' },
        hasActions: ModalBaseActions.NONE,
        closeOnEscOrBackdropClick: false,
      });
    }
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.mobileToolbarQuery.removeListener(this._mobileToolbarQueryListener);
    super.ngOnDestroy();
  }
}
